import { Button, Grid, GridColumn, Heading, IButtonProps, Notice, Separator, Text } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import useStores from '../stores/UseStores';
import { isPasswordStrong } from '../util/ValidationUtil';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import { PasswordFormFields } from '../shared/PasswordFormFields';
import { confirmPwFieldHelper, strongPwFieldHelper } from '../util/FieldHelperUtil';

const ResetPassword = observer((): React.JSX.Element => {
  const {
    resetPasswordStore,
    routeStore,
    translateStore: { translate },
    uiStore: { showErrorNoticeWithErroredFields, hideNotices },
  } = useStores();
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (routeStore.historyLength()) {
      hideNotices();
    }
  }, []);

  const getPasswordHelper = (forceIsDirty?: boolean) =>
    strongPwFieldHelper(translate, resetPasswordStore.emailLoginPasswords.password, null, forceIsDirty ?? hasSubmitted);
  const getConfirmPwHelper = (forceIsDirty?: boolean) =>
    confirmPwFieldHelper(
      translate,
      resetPasswordStore.emailLoginPasswords.password,
      resetPasswordStore.emailLoginPasswords.confirmPassword,
      forceIsDirty ?? hasSubmitted,
    );

  const getCancellationButtonProps = (): IButtonProps => {
    const cancelUrl = routeStore.homePageWithParams;

    return {
      text: translate('password.cancel_button_label'),
      variant: 'secondary',
      type: 'withdrawal',
      icon: '#arrow-left',
      url: cancelUrl,
      onClick: (event) => {
        event.preventDefault();
        routeStore.route(cancelUrl);
      },
    };
  };

  const confirmPasswordFieldIsEqual = () => {
    return resetPasswordStore.emailLoginPasswords.password === resetPasswordStore.emailLoginPasswords.confirmPassword;
  };

  const isInvalid = () => {
    return !isPasswordStrong(resetPasswordStore.emailLoginPasswords.password) || !confirmPasswordFieldIsEqual();
  };

  const handleFormSubmit = (event: React.FormEvent): void => {
    runInTeliaRumService('Parooli vahetamine', () => {
      setHasSubmitted(true);
      event.preventDefault();
      if (isInvalid()) {
        showErrorNoticeWithErroredFields('form.error', [
          getPasswordHelper(true),
          !!resetPasswordStore.emailLoginPasswords.password && isPasswordStrong(resetPasswordStore.emailLoginPasswords.password)
            ? getConfirmPwHelper(true)
            : undefined,
        ]);
        return;
      }
      resetPasswordStore.handleLogin();
    });
  };

  const renderPasswordRules = () => {
    return (
      <GridColumn width={['max']}>
        {Array.from({ length: 4 }, (i, idx) => `password.rules.${idx}`).map((text, idx) => (
          <Notice size="small" key={`rule-${idx}`}>
            {translate(text)}
          </Notice>
        ))}
      </GridColumn>
    );
  };

  const renderForm = (id: string, children: React.JSX.Element) => {
    return (
      <GridColumn width={['sm-6', 'md-8', 'lg-6']}>
        <form onSubmit={handleFormSubmit} id={id}>
          {children}
        </form>
      </GridColumn>
    );
  };

  const renderSubmitButton = () => {
    return (
      <Button
        text={translate('password.submit_button_label')}
        isLoading={resetPasswordStore.submitting}
        attributes={{ type: 'submit' }}
        iconRight="#arrow-right"
        type="primary"
      />
    );
  };

  if (!routeStore.decodedParams['key']) {
    return (
      <div>
        <Separator spacing="large" color="invisible" />
        <Notice type="error">
          <b>{translate('error.general')}</b>
          <>
            <br />
            {translate('change_password.login_needed')}
          </>
        </Notice>
      </div>
    );
  }

  return (
    <Grid>
      <GridColumn width={['xs-12', 'md-8']}>
        <Text>
          <Grid noWrap={true} align={['middle-xs']}>
            <GridColumn width={['min']}>
              <Button {...getCancellationButtonProps()} />
            </GridColumn>
            <GridColumn width={['max']}>
              <Heading element="h1" className="h2 text-primary">
                {translate('password.header')}
              </Heading>
            </GridColumn>
          </Grid>
          <Separator color="invisible" spacing="xsmall" />
          <p className="h4">{translate('password.description')}</p>
          <Separator color="invisible" spacing="xsmall" />
          <Grid>
            {renderPasswordRules()}
            {renderForm(
              'resetPassword',
              <Text>
                <PasswordFormFields
                  translate={translate}
                  emailLoginPasswords={resetPasswordStore.emailLoginPasswords}
                  hasSubmitted={hasSubmitted}
                  passwordHelper={getPasswordHelper()}
                  confirmPasswordHelper={getConfirmPwHelper()}
                />
                {renderSubmitButton()}
              </Text>,
            )}
          </Grid>
        </Text>
      </GridColumn>
    </Grid>
  );
});

export default ResetPassword;
